import {
  DesignFloorPlan,
  DrawingElement,
  FloorPlanPoint,
  FloorPlanReferenceImage,
  FloorplanData,
  Line,
  Opening,
  Rectangle,
  Room,
  Wall,
} from '../types';

/**
 * Fixed display scale for the designer canvas (not real-world screen size).
 * 50 px = 1 m  →  a 5 m wall is 250 px; a ~10 m room spans ~500 px.
 */
export const DEFAULT_PIXELS_PER_METER = 50;

/** Clamp / normalize any stored scale back to the fixed display scale */
export function normalizePixelsPerMeter(_value?: number): number {
  return DEFAULT_PIXELS_PER_METER;
}
export const DEFAULT_DOOR_WIDTH_M = 0.9;
export const DEFAULT_WALL_THICKNESS_M = 0.1;
/** True wall corners — generous so new walls easily join existing ones */
export const ENDPOINT_SNAP_PX = 22;
export const MIDPOINT_SNAP_PX = 12;
export const WALL_SNAP_PX = 12;
/** How close the cursor must be to an axis / equal-length target to snap */
export const ALIGN_SNAP_PX = 12;
export const LENGTH_SNAP_PX = 10;

export type DraftSnapKind = 'endpoint' | 'axis' | 'length' | 'edge' | 'grid' | null;

export interface DraftSnapResult {
  point: FloorPlanPoint;
  kind: DraftSnapKind;
  /** Reference endpoint that caused an axis / length snap (for guide lines) */
  matchedPoint?: FloorPlanPoint;
  /** Optional second guide point (e.g. opposite corner) */
  guidePoint?: FloorPlanPoint;
  /** True when this point was snapped onto the designer grid */
  gridSnapped?: boolean;
}

export function createEmptyFloorPlan(
  units: 'metric' | 'imperial' = 'metric',
  _pixelsPerMeter = DEFAULT_PIXELS_PER_METER
): DesignFloorPlan {
  return {
    version: 1,
    units,
    pixelsPerMeter: DEFAULT_PIXELS_PER_METER,
    // Display scale is fixed for on-screen fit; lengths are always in m/ft
    calibrated: true,
    walls: [],
    openings: [],
    rooms: [],
    referenceImages: [],
  };
}

/** Convert legacy singular `referenceImage` into `referenceImages` and drop the singular field. */
export function normalizeFloorPlanReferenceImages(fp: DesignFloorPlan): DesignFloorPlan {
  const hasArray = Array.isArray(fp.referenceImages) && fp.referenceImages.length > 0;
  if (hasArray) {
    if (!fp.referenceImage) return fp;
    const { referenceImage: _legacy, ...rest } = fp;
    return rest;
  }

  if (fp.referenceImage?.url) {
    const legacy = fp.referenceImage;
    const { referenceImage: _legacy, ...rest } = fp;
    return {
      ...rest,
      referenceImages: [
        {
          id: 'ref-legacy',
          url: legacy.url,
          naturalWidth: legacy.width,
          naturalHeight: legacy.height,
          x: 0,
          y: 0,
          width: legacy.width,
          height: legacy.height,
          opacity: legacy.opacity ?? 0.45,
        },
      ],
    };
  }

  if (fp.referenceImages) return fp;
  return { ...fp, referenceImages: [] };
}

export function getFloorPlanReferenceImages(fp: DesignFloorPlan): FloorPlanReferenceImage[] {
  return normalizeFloorPlanReferenceImages(fp).referenceImages ?? [];
}

export function pixelDistance(a: FloorPlanPoint, b: FloorPlanPoint): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.hypot(dx, dy);
}

export function wallLengthPx(wall: Wall): number {
  return pixelDistance(wall.a, wall.b);
}

export function wallLengthMeters(wall: Wall, pixelsPerMeter: number): number {
  if (pixelsPerMeter <= 0) return 0;
  return wallLengthPx(wall) / pixelsPerMeter;
}

export function canvasDistanceToMeters(px: number, pixelsPerMeter: number): number {
  if (pixelsPerMeter <= 0) return 0;
  return px / pixelsPerMeter;
}

export function metersToCanvas(meters: number, pixelsPerMeter: number): number {
  return meters * pixelsPerMeter;
}

export function pointAlongWall(wall: Wall, t: number): FloorPlanPoint {
  return {
    x: wall.a.x + (wall.b.x - wall.a.x) * t,
    y: wall.a.y + (wall.b.y - wall.a.y) * t,
  };
}

export const DEFAULT_WALL_LABEL_OUT_PX = 12;
const DEFAULT_LABEL_WIDTH_PX = 48;
const DEFAULT_LABEL_HEIGHT_PX = 18;
const WALL_LABEL_GAP_PX = 6;

export function wallUnitAxes(wall: Wall): {
  ux: number;
  uy: number;
  nx: number;
  ny: number;
} {
  const len = wallLengthPx(wall);
  if (len < 1e-6) return { ux: 1, uy: 0, nx: 0, ny: -1 };
  const ux = (wall.b.x - wall.a.x) / len;
  const uy = (wall.b.y - wall.a.y) / len;
  return { ux, uy, nx: -uy, ny: ux };
}

/** Fixed distance from wall centerline so the label sits beside it, not on it. */
export function wallLabelOutDistance(
  wall: Wall,
  labelWidth = DEFAULT_LABEL_WIDTH_PX,
  labelHeight = DEFAULT_LABEL_HEIGHT_PX
): number {
  const { nx, ny } = wallUnitAxes(wall);
  return Math.abs(nx) * (labelWidth / 2) + Math.abs(ny) * (labelHeight / 2) + WALL_LABEL_GAP_PX;
}

function defaultWallLabelOut(
  wall: Wall,
  labelWidth?: number,
  labelHeight?: number
): number {
  const { nx, ny } = wallUnitAxes(wall);
  const preferUpOrRight = Math.abs(ny) >= Math.abs(nx) ? ny <= 0 : nx >= 0;
  return (preferUpOrRight ? 1 : -1) * wallLabelOutDistance(wall, labelWidth, labelHeight);
}

function clampedLabelOut(
  wall: Wall,
  out: number,
  labelWidth?: number,
  labelHeight?: number
): number {
  return (out < 0 ? -1 : 1) * wallLabelOutDistance(wall, labelWidth, labelHeight);
}

export function wallLabelOffset(
  wall: Wall,
  labelWidth?: number,
  labelHeight?: number
): { along: number; out: number } {
  return {
    along: wall.labelAlong ?? 0,
    out: clampedLabelOut(
      wall,
      wall.labelOut ?? defaultWallLabelOut(wall, labelWidth, labelHeight),
      labelWidth,
      labelHeight
    ),
  };
}

export function wallLabelPosition(
  wall: Wall,
  labelWidth?: number,
  labelHeight?: number
): FloorPlanPoint {
  const { ux, uy, nx, ny } = wallUnitAxes(wall);
  const { along, out } = wallLabelOffset(wall, labelWidth, labelHeight);
  const midX = (wall.a.x + wall.b.x) / 2;
  const midY = (wall.a.y + wall.b.y) / 2;
  return {
    x: midX + along * ux + out * nx,
    y: midY + along * uy + out * ny,
  };
}

export function wallLabelOffsetFromPoint(
  wall: Wall,
  point: FloorPlanPoint,
  labelWidth?: number,
  labelHeight?: number
): { along: number; out: number } {
  const { ux, uy, nx, ny } = wallUnitAxes(wall);
  const midX = (wall.a.x + wall.b.x) / 2;
  const midY = (wall.a.y + wall.b.y) / 2;
  const dx = point.x - midX;
  const dy = point.y - midY;
  const half = wallLengthPx(wall) / 2;
  const pad = Math.max(0, half - 8);
  let along = dx * ux + dy * uy;
  along = Math.max(-pad, Math.min(pad, along));
  if (Math.abs(along) < 6) along = 0;
  const side = dx * nx + dy * ny < 0 ? -1 : 1;
  return { along, out: side * wallLabelOutDistance(wall, labelWidth, labelHeight) };
}

export function applyOrtho(
  from: FloorPlanPoint,
  to: FloorPlanPoint,
  freeAngle: boolean
): FloorPlanPoint {
  if (freeAngle) return to;
  const dx = Math.abs(to.x - from.x);
  const dy = Math.abs(to.y - from.y);
  if (dx >= dy) {
    return { x: to.x, y: from.y };
  }
  return { x: from.x, y: to.y };
}

export function snapPointToGrid(point: FloorPlanPoint, gridSize: number): FloorPlanPoint {
  const g = Math.max(1, gridSize);
  return {
    x: Math.round(point.x / g) * g,
    y: Math.round(point.y / g) * g,
  };
}

/** Snap a draft endpoint onto the grid without breaking ortho from `from`. */
export function snapDraftPointToGrid(
  from: FloorPlanPoint | undefined,
  point: FloorPlanPoint,
  gridSize: number,
  freeAngle: boolean
): FloorPlanPoint {
  if (gridSize <= 0) return point;
  const g = Math.max(1, gridSize);
  if (!from || freeAngle) return snapPointToGrid(point, g);
  if (Math.abs(point.y - from.y) < 0.5) {
    return { x: Math.round(point.x / g) * g, y: from.y };
  }
  if (Math.abs(point.x - from.x) < 0.5) {
    return { x: from.x, y: Math.round(point.y / g) * g };
  }
  return snapPointToGrid(point, g);
}

/** Adjust a wall-drag delta so the moved vertex / wall lands on the grid. */
export function snapDragDeltaToGrid(args: {
  mode: 'push' | 'corner';
  wall?: Wall | null;
  vertexPoint?: FloorPlanPoint;
  delta: FloorPlanPoint;
  gridSize: number;
}): FloorPlanPoint {
  const g = Math.max(1, args.gridSize);
  if (args.mode === 'corner' && args.vertexPoint) {
    const snapped = snapPointToGrid(
      {
        x: args.vertexPoint.x + args.delta.x,
        y: args.vertexPoint.y + args.delta.y,
      },
      g
    );
    return {
      x: snapped.x - args.vertexPoint.x,
      y: snapped.y - args.vertexPoint.y,
    };
  }
  if (args.mode === 'push' && args.wall) {
    if (wallAxis(args.wall) === 'h') {
      const ny = Math.round((args.wall.a.y + args.delta.y) / g) * g;
      return { x: 0, y: ny - args.wall.a.y };
    }
    const nx = Math.round((args.wall.a.x + args.delta.x) / g) * g;
    return { x: nx - args.wall.a.x, y: 0 };
  }
  return args.delta;
}

export function collectSnapPoints(walls: Wall[]): FloorPlanPoint[] {
  const points: FloorPlanPoint[] = [];
  for (const wall of walls) {
    points.push(wall.a, wall.b);
    points.push(pointAlongWall(wall, 0.5));
  }
  return points;
}

/** Unique wall corner points (no midpoints) — for joining new walls */
export function collectUniqueEndpoints(walls: Wall[]): FloorPlanPoint[] {
  const map = new Map<string, FloorPlanPoint>();
  for (const wall of walls) {
    for (const p of [wall.a, wall.b]) {
      const key = `${Math.round(p.x * 10) / 10},${Math.round(p.y * 10) / 10}`;
      if (!map.has(key)) map.set(key, p);
    }
  }
  return Array.from(map.values());
}

export function findNearestEndpoint(
  point: FloorPlanPoint,
  walls: Wall[],
  snapPx = ENDPOINT_SNAP_PX,
  exclude?: FloorPlanPoint | null
): FloorPlanPoint | null {
  let best: FloorPlanPoint | null = null;
  let bestDist = snapPx;
  for (const ep of collectUniqueEndpoints(walls)) {
    if (exclude && pointsEqual(ep, exclude, 1)) continue;
    const d = pixelDistance(point, ep);
    if (d < bestDist) {
      bestDist = d;
      best = ep;
    }
  }
  return best;
}

export function snapPoint(
  point: FloorPlanPoint,
  walls: Wall[],
  snapPx = ENDPOINT_SNAP_PX
): FloorPlanPoint {
  return snapPointDetailed(point, walls, snapPx).point;
}

export function snapPointDetailed(
  point: FloorPlanPoint,
  walls: Wall[],
  snapPx = ENDPOINT_SNAP_PX
): DraftSnapResult {
  // 1) Prefer true wall endpoints (corners) — strongest snap for joining walls
  const endpoint = findNearestEndpoint(point, walls, snapPx);
  if (endpoint) {
    return { point: endpoint, kind: 'endpoint', matchedPoint: endpoint };
  }

  // 2) Midpoints
  let best = point;
  let bestDist = MIDPOINT_SNAP_PX;
  let kind: DraftSnapKind = null;
  let matchedPoint: FloorPlanPoint | undefined;

  for (const wall of walls) {
    const mid = pointAlongWall(wall, 0.5);
    const d = pixelDistance(point, mid);
    if (d < bestDist) {
      bestDist = d;
      best = mid;
      kind = 'endpoint';
      matchedPoint = mid;
    }
  }
  if (kind) {
    return { point: best, kind, matchedPoint };
  }

  // 3) Anywhere along an edge
  for (const wall of walls) {
    const proj = projectPointOnSegment(point, wall.a, wall.b);
    const d = pixelDistance(point, proj.point);
    if (d < WALL_SNAP_PX) {
      // If projection is almost at a corner, snap to the exact corner
      if (proj.t < 0.05) {
        return { point: wall.a, kind: 'endpoint', matchedPoint: wall.a };
      }
      if (proj.t > 0.95) {
        return { point: wall.b, kind: 'endpoint', matchedPoint: wall.b };
      }
      return { point: proj.point, kind: 'edge', matchedPoint: proj.point };
    }
  }

  return { point, kind: null };
}

/**
 * CAD-style snap while drafting a wall from `from` toward the cursor.
 * Priority: endpoint → axis align to opposite points → equal length → edge.
 */
export function snapDraftEndpoint(
  from: FloorPlanPoint,
  rawCursor: FloorPlanPoint,
  walls: Wall[],
  freeAngle: boolean,
  snapPx = ALIGN_SNAP_PX,
  endpointSnapPx = ENDPOINT_SNAP_PX
): DraftSnapResult {
  // 1) Snap to existing wall endpoints from the raw cursor (join / close corners)
  const nearCursor = findNearestEndpoint(rawCursor, walls, endpointSnapPx, from);
  if (nearCursor) {
    return {
      point: nearCursor,
      kind: 'endpoint',
      matchedPoint: nearCursor,
    };
  }

  // 2) Ortho constraint
  const ortho = applyOrtho(from, rawCursor, freeAngle);
  const isHorizontal = !freeAngle && Math.abs(ortho.y - from.y) < 0.5;
  const isVertical = !freeAngle && Math.abs(ortho.x - from.x) < 0.5;

  // 2b) Also snap if the ortho-constrained point is near an endpoint
  const nearOrtho = findNearestEndpoint(ortho, walls, endpointSnapPx, from);
  if (nearOrtho) {
    // Allow off-ortho close for joining corners; otherwise keep ortho if endpoint is on-axis
    if (freeAngle || pointsEqual(applyOrtho(from, nearOrtho, false), nearOrtho, 1.5)) {
      return {
        point: nearOrtho,
        kind: 'endpoint',
        matchedPoint: nearOrtho,
      };
    }
  }

  // 3) Axis alignment: snap length so the free end lines up with another endpoint
  const endpoints = collectUniqueEndpoints(walls);
  let bestAxis: DraftSnapResult | null = null;
  let bestAxisDist = snapPx;

  for (const ep of endpoints) {
    if (pointsEqual(ep, from, 1)) continue;

    if (isHorizontal || freeAngle) {
      const distToGuide = Math.abs(ortho.x - ep.x);
      if (distToGuide < bestAxisDist && Math.abs(ep.x - from.x) > 1) {
        const candidate = freeAngle
          ? { x: ep.x, y: ortho.y }
          : { x: ep.x, y: from.y };
        bestAxisDist = distToGuide;
        bestAxis = {
          point: candidate,
          kind: 'axis',
          matchedPoint: ep,
          guidePoint: { x: ep.x, y: from.y },
        };
      }
    }

    if (isVertical || freeAngle) {
      const distToGuide = Math.abs(ortho.y - ep.y);
      if (distToGuide < bestAxisDist && Math.abs(ep.y - from.y) > 1) {
        const candidate = freeAngle
          ? { x: ortho.x, y: ep.y }
          : { x: from.x, y: ep.y };
        bestAxisDist = distToGuide;
        bestAxis = {
          point: candidate,
          kind: 'axis',
          matchedPoint: ep,
          guidePoint: { x: from.x, y: ep.y },
        };
      }
    }
  }

  if (bestAxis) {
    // Landing on a corner → exact endpoint join
    for (const ep of endpoints) {
      if (pointsEqual(bestAxis.point, ep, 2)) {
        return { point: ep, kind: 'endpoint', matchedPoint: ep };
      }
    }
    return bestAxis;
  }

  // 4) Equal-length snap
  const dirX = ortho.x - from.x;
  const dirY = ortho.y - from.y;
  const curLen = Math.hypot(dirX, dirY);
  if (curLen > 2) {
    const ux = dirX / curLen;
    const uy = dirY / curLen;
    let bestLenDist = LENGTH_SNAP_PX;
    let bestLenPoint: FloorPlanPoint | null = null;
    let matched: FloorPlanPoint | undefined;

    for (const wall of walls) {
      const wLen = wallLengthPx(wall);
      if (wLen < 2) continue;
      const delta = Math.abs(curLen - wLen);
      if (delta < bestLenDist) {
        bestLenDist = delta;
        bestLenPoint = { x: from.x + ux * wLen, y: from.y + uy * wLen };
        matched = wall.b;
      }
    }

    if (bestLenPoint) {
      // If equal-length lands on an endpoint, prefer the endpoint
      const landed = findNearestEndpoint(bestLenPoint, walls, 3, from);
      if (landed) {
        return { point: landed, kind: 'endpoint', matchedPoint: landed };
      }
      return {
        point: bestLenPoint,
        kind: 'length',
        matchedPoint: matched,
      };
    }
  }

  // 5) Soft edge snap on the ortho point
  const soft = snapPointDetailed(ortho, walls, endpointSnapPx);
  if (soft.kind === 'endpoint') return soft;
  if (soft.kind === 'edge') {
    if (!freeAngle) {
      const kept = applyOrtho(from, soft.point, false);
      if (pixelDistance(kept, soft.point) < 1) return soft;
    } else {
      return soft;
    }
  }

  return { point: ortho, kind: null };
}

export function projectPointOnSegment(
  p: FloorPlanPoint,
  a: FloorPlanPoint,
  b: FloorPlanPoint
): { point: FloorPlanPoint; t: number; distance: number } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) {
    return { point: a, t: 0, distance: pixelDistance(p, a) };
  }
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const point = { x: a.x + t * dx, y: a.y + t * dy };
  return { point, t, distance: pixelDistance(p, point) };
}

export function findNearestWall(
  point: FloorPlanPoint,
  walls: Wall[],
  maxDistPx = 16
): { wall: Wall; t: number; distance: number } | null {
  let best: { wall: Wall; t: number; distance: number } | null = null;
  for (const wall of walls) {
    const proj = projectPointOnSegment(point, wall.a, wall.b);
    if (proj.distance <= maxDistPx && (!best || proj.distance < best.distance)) {
      best = { wall, t: proj.t, distance: proj.distance };
    }
  }
  return best;
}

export function pointsEqual(a: FloorPlanPoint, b: FloorPlanPoint, epsilon = 0.5): boolean {
  return Math.abs(a.x - b.x) < epsilon && Math.abs(a.y - b.y) < epsilon;
}

/** Interior intersection of two segments, or null if they only touch at an endpoint / don't cross. */
export function segmentIntersection(
  a1: FloorPlanPoint,
  a2: FloorPlanPoint,
  b1: FloorPlanPoint,
  b2: FloorPlanPoint
): { point: FloorPlanPoint; tA: number; tB: number } | null {
  const dax = a2.x - a1.x;
  const day = a2.y - a1.y;
  const dbx = b2.x - b1.x;
  const dby = b2.y - b1.y;
  const denom = dax * dby - day * dbx;
  if (Math.abs(denom) < 1e-8) return null;
  const tA = ((b1.x - a1.x) * dby - (b1.y - a1.y) * dbx) / denom;
  const tB = ((b1.x - a1.x) * day - (b1.y - a1.y) * dax) / denom;
  if (tA <= 0.04 || tA >= 0.96 || tB <= 0.04 || tB >= 0.96) return null;
  return {
    point: { x: a1.x + tA * dax, y: a1.y + tA * day },
    tA,
    tB,
  };
}

const MERGE_PARALLEL_DOT = 0.98; // ~11°
const MERGE_LINE_DIST_PX = 4;
const MERGE_GAP_PX = 8;

export type MergeWallsError = 'not-colinear' | 'not-connected';

export type MergeWallGeometryResult =
  | { a: FloorPlanPoint; b: FloorPlanPoint }
  | { error: MergeWallsError };

function unitDirection(from: FloorPlanPoint, to: FloorPlanPoint): FloorPlanPoint | null {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return null;
  return { x: dx / len, y: dy / len };
}

function projectScalar(p: FloorPlanPoint, origin: FloorPlanPoint, dir: FloorPlanPoint): number {
  return (p.x - origin.x) * dir.x + (p.y - origin.y) * dir.y;
}

/** Distance from point to infinite line through origin with unit direction dir */
function distanceToLine(p: FloorPlanPoint, origin: FloorPlanPoint, dir: FloorPlanPoint): number {
  return Math.abs((p.x - origin.x) * dir.y - (p.y - origin.y) * dir.x);
}

/**
 * Merge two walls into one segment spanning their farthest endpoints.
 * Requires the walls to be colinear (or nearly) and to touch or slightly overlap.
 */
export function mergeWallGeometry(w1: Wall, w2: Wall): MergeWallGeometryResult {
  const dir1 = unitDirection(w1.a, w1.b);
  const dir2 = unitDirection(w2.a, w2.b);
  if (!dir1 || !dir2) return { error: 'not-colinear' };

  const absDot = Math.abs(dir1.x * dir2.x + dir1.y * dir2.y);
  if (absDot < MERGE_PARALLEL_DOT) return { error: 'not-colinear' };

  if (
    distanceToLine(w2.a, w1.a, dir1) > MERGE_LINE_DIST_PX ||
    distanceToLine(w2.b, w1.a, dir1) > MERGE_LINE_DIST_PX
  ) {
    return { error: 'not-colinear' };
  }

  const t1a = projectScalar(w1.a, w1.a, dir1);
  const t1b = projectScalar(w1.b, w1.a, dir1);
  const t2a = projectScalar(w2.a, w1.a, dir1);
  const t2b = projectScalar(w2.b, w1.a, dir1);
  const min1 = Math.min(t1a, t1b);
  const max1 = Math.max(t1a, t1b);
  const min2 = Math.min(t2a, t2b);
  const max2 = Math.max(t2a, t2b);
  const gap = Math.max(min1, min2) - Math.min(max1, max2);
  if (gap > MERGE_GAP_PX) return { error: 'not-connected' };

  const pts = [w1.a, w1.b, w2.a, w2.b].map((p) => ({
    p,
    t: projectScalar(p, w1.a, dir1),
  }));
  pts.sort((a, b) => a.t - b.t);
  return { a: { ...pts[0].p }, b: { ...pts[pts.length - 1].p } };
}

export function remapOpeningParameter(opening: Opening, oldWall: Wall, newWall: Wall): number {
  const p = pointAlongWall(oldWall, opening.t);
  return projectPointOnSegment(p, newWall.a, newWall.b).t;
}

export const VERTEX_CLUSTER_PX = 6;
export const MIN_WALL_LENGTH_PX = 8;
export const T_JUNCTION_HIT_PX = 10;
export const WALL_DRAG_THRESHOLD_PX = 4;
export const CORNER_HANDLE_HIT_PX = 10;
const COLINEAR_RUN_PX = 8;

export type WallEndpointRef = { wallId: string; end: 'a' | 'b' };

export interface VertexCluster {
  key: string;
  point: FloorPlanPoint;
  refs: WallEndpointRef[];
}

export function vertexKey(p: FloorPlanPoint): string {
  return `${Math.round(p.x * 2) / 2},${Math.round(p.y * 2) / 2}`;
}

export function wallAxis(wall: Wall): 'h' | 'v' {
  return Math.abs(wall.b.x - wall.a.x) >= Math.abs(wall.b.y - wall.a.y) ? 'h' : 'v';
}

export function wallResizeCursor(wall: Wall): string {
  return wallAxis(wall) === 'h' ? 'ns-resize' : 'ew-resize';
}

export function cornerResizeCursor(vertex: VertexCluster, walls: Wall[]): string {
  const wallById = new Map(walls.map((w) => [w.id, w]));
  let hasLeft = false;
  let hasRight = false;
  let hasUp = false;
  let hasDown = false;
  for (let i = 0; i < vertex.refs.length; i++) {
    const ref = vertex.refs[i];
    const wall = wallById.get(ref.wallId);
    if (!wall) continue;
    const far = ref.end === 'a' ? wall.b : wall.a;
    const dx = far.x - vertex.point.x;
    const dy = far.y - vertex.point.y;
    if (Math.abs(dx) >= Math.abs(dy)) {
      if (dx < 0) hasLeft = true;
      else hasRight = true;
    } else if (dy < 0) {
      hasUp = true;
    } else {
      hasDown = true;
    }
  }
  const nwse = (hasRight && hasDown) || (hasLeft && hasUp);
  return nwse ? 'nwse-resize' : 'nesw-resize';
}

export function clusterVertices(walls: Wall[], epsilon = VERTEX_CLUSTER_PX): VertexCluster[] {
  const items: Array<{ wallId: string; end: 'a' | 'b'; point: FloorPlanPoint }> = [];
  for (const wall of walls) {
    items.push({ wallId: wall.id, end: 'a', point: wall.a });
    items.push({ wallId: wall.id, end: 'b', point: wall.b });
  }
  if (items.length === 0) return [];

  const parent = items.map((_, i) => i);
  const find = (i: number): number => {
    let root = i;
    while (parent[root] !== root) root = parent[root];
    let cur = i;
    while (parent[cur] !== root) {
      const next = parent[cur];
      parent[cur] = root;
      cur = next;
    }
    return root;
  };
  const union = (i: number, j: number) => {
    const ri = find(i);
    const rj = find(j);
    if (ri !== rj) parent[ri] = rj;
  };

  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      if (pointsEqual(items[i].point, items[j].point, epsilon)) union(i, j);
    }
  }

  const groups = new Map<number, VertexCluster>();
  for (let i = 0; i < items.length; i++) {
    const root = find(i);
    let group = groups.get(root);
    if (!group) {
      group = {
        key: vertexKey(items[i].point),
        point: { ...items[i].point },
        refs: [],
      };
      groups.set(root, group);
    }
    group.refs.push({ wallId: items[i].wallId, end: items[i].end });
  }
  return Array.from(groups.values());
}

export function clusterAtPoint(
  clusters: VertexCluster[],
  point: FloorPlanPoint,
  epsilon = VERTEX_CLUSTER_PX * 2
): VertexCluster | null {
  let best: VertexCluster | null = null;
  let bestDist = epsilon;
  for (const cluster of clusters) {
    const d = pixelDistance(cluster.point, point);
    if (d <= bestDist) {
      bestDist = d;
      best = cluster;
    }
  }
  return best;
}

function tJunctionRefsOnWall(walls: Wall[], wall: Wall): WallEndpointRef[] {
  const refs: WallEndpointRef[] = [];
  for (const other of walls) {
    if (other.id === wall.id) continue;
    const ends: Array<'a' | 'b'> = ['a', 'b'];
    for (let i = 0; i < ends.length; i++) {
      const end = ends[i];
      const p = other[end];
      const proj = projectPointOnSegment(p, wall.a, wall.b);
      if (proj.distance > T_JUNCTION_HIT_PX) continue;
      if (proj.t < 0.04 || proj.t > 0.96) continue;
      refs.push({ wallId: other.id, end });
    }
  }
  return refs;
}

function wallLineCoord(wall: Wall): number {
  if (wallAxis(wall) === 'v') return (wall.a.x + wall.b.x) / 2;
  return (wall.a.y + wall.b.y) / 2;
}

function areColinearSameAxis(a: Wall, b: Wall, epsilon = COLINEAR_RUN_PX): boolean {
  if (wallAxis(a) !== wallAxis(b)) return false;
  return Math.abs(wallLineCoord(a) - wallLineCoord(b)) <= epsilon;
}

/** All walls that form one straight connected run with `startId` (same axis, colinear, shared corners). */
export function colinearRunIds(walls: Wall[], startId: string): string[] {
  const start = walls.find((w) => w.id === startId);
  if (!start) return [];
  const clusters = clusterVertices(walls);
  const wallById = new Map(walls.map((w) => [w.id, w]));
  const visited = new Set<string>([startId]);
  const queue = [startId];

  while (queue.length > 0) {
    const id = queue.shift()!;
    const wall = wallById.get(id);
    if (!wall) continue;
    const ends: FloorPlanPoint[] = [wall.a, wall.b];
    for (let i = 0; i < ends.length; i++) {
      const cluster = clusterAtPoint(clusters, ends[i]);
      if (!cluster) continue;
      for (let j = 0; j < cluster.refs.length; j++) {
        const otherId = cluster.refs[j].wallId;
        if (visited.has(otherId)) continue;
        const other = wallById.get(otherId);
        if (!other || !areColinearSameAxis(start, other)) continue;
        visited.add(otherId);
        queue.push(otherId);
      }
    }
  }
  return Array.from(visited);
}

function applyDeltaToRefs(walls: Wall[], refs: WallEndpointRef[], delta: FloorPlanPoint): Wall[] {
  const refSet = new Set(refs.map((r) => `${r.wallId}:${r.end}`));
  return walls.map((w) => {
    const moveA = refSet.has(`${w.id}:a`);
    const moveB = refSet.has(`${w.id}:b`);
    if (!moveA && !moveB) return w;
    return {
      ...w,
      a: moveA ? { x: w.a.x + delta.x, y: w.a.y + delta.y } : w.a,
      b: moveB ? { x: w.b.x + delta.x, y: w.b.y + delta.y } : w.b,
    };
  });
}

function anyWallTooShort(walls: Wall[]): boolean {
  for (let i = 0; i < walls.length; i++) {
    if (wallLengthPx(walls[i]) < MIN_WALL_LENGTH_PX) return true;
  }
  return false;
}

function constrainPushDelta(wall: Wall, delta: FloorPlanPoint, freeAngle: boolean): FloorPlanPoint {
  if (freeAngle) return { ...delta };
  if (wallAxis(wall) === 'h') return { x: 0, y: delta.y };
  return { x: delta.x, y: 0 };
}

/** Translate a wall along its normal; colinear run, shared corners, and T-junctions follow. */
export function pushWall(
  walls: Wall[],
  wallId: string,
  delta: FloorPlanPoint,
  freeAngle = false,
  extend = false
): Wall[] | null {
  const wall = walls.find((w) => w.id === wallId);
  if (!wall) return null;
  const d = constrainPushDelta(wall, delta, freeAngle);
  if (Math.abs(d.x) < 0.01 && Math.abs(d.y) < 0.01) return walls;

  const runIds = extend ? [wallId] : colinearRunIds(walls, wallId);
  const clusters = clusterVertices(walls);
  const refs: WallEndpointRef[] = [];
  const seenRef = new Set<string>();
  const addRef = (ref: WallEndpointRef) => {
    const key = `${ref.wallId}:${ref.end}`;
    if (seenRef.has(key)) return;
    seenRef.add(key);
    refs.push(ref);
  };

  const addCluster = (runWall: Wall, cluster: VertexCluster | null, end: 'a' | 'b') => {
    if (!cluster) {
      addRef({ wallId: runWall.id, end });
      return;
    }
    for (let i = 0; i < cluster.refs.length; i++) {
      const ref = cluster.refs[i];
      if (ref.wallId === runWall.id) {
        addRef(ref);
        continue;
      }
      if (extend) {
        const other = walls.find((w) => w.id === ref.wallId);
        if (!other || areColinearSameAxis(wall, other)) continue;
      }
      addRef(ref);
    }
  };

  for (let i = 0; i < runIds.length; i++) {
    const runWall = walls.find((w) => w.id === runIds[i]);
    if (!runWall) continue;
    addCluster(runWall, clusterAtPoint(clusters, runWall.a), 'a');
    addCluster(runWall, clusterAtPoint(clusters, runWall.b), 'b');
    tJunctionRefsOnWall(walls, runWall).forEach(addRef);
  }

  const next = applyDeltaToRefs(walls, refs, d);
  if (anyWallTooShort(next)) return null;
  return next;
}

/** Move a corner. Ortho: dx pushes vertical walls, dy pushes horizontal walls. */
export function moveCorner(
  walls: Wall[],
  vertexPoint: FloorPlanPoint,
  delta: FloorPlanPoint,
  freeAngle = false,
  extend = false,
  preferredWallId?: string
): Wall[] | null {
  const clusters = clusterVertices(walls);
  const cluster = clusterAtPoint(clusters, vertexPoint, CORNER_HANDLE_HIT_PX);
  if (!cluster) return null;

  if (freeAngle) {
    const next = applyDeltaToRefs(walls, cluster.refs, delta);
    if (anyWallTooShort(next)) return null;
    return next;
  }

  if (extend && preferredWallId) {
    return pushWall(walls, preferredWallId, delta, false, true);
  }

  const wallById = new Map(walls.map((w) => [w.id, w]));
  const seen = new Set<string>();
  const verticalIds: string[] = [];
  const horizontalIds: string[] = [];
  for (let i = 0; i < cluster.refs.length; i++) {
    const ref = cluster.refs[i];
    const wall = wallById.get(ref.wallId);
    if (!wall || seen.has(wall.id)) continue;
    seen.add(wall.id);
    if (wallAxis(wall) === 'v') verticalIds.push(wall.id);
    else horizontalIds.push(wall.id);
  }

  let current = walls;
  const pushed = new Set<string>();
  if (Math.abs(delta.x) >= 0.01) {
    for (let i = 0; i < verticalIds.length; i++) {
      if (pushed.has(verticalIds[i])) continue;
      colinearRunIds(current, verticalIds[i]).forEach((id) => pushed.add(id));
      const next = pushWall(current, verticalIds[i], { x: delta.x, y: 0 }, false, extend);
      if (!next) return null;
      current = next;
    }
  }
  if (Math.abs(delta.y) >= 0.01) {
    for (let i = 0; i < horizontalIds.length; i++) {
      if (pushed.has(horizontalIds[i])) continue;
      colinearRunIds(current, horizontalIds[i]).forEach((id) => pushed.add(id));
      const next = pushWall(current, horizontalIds[i], { x: 0, y: delta.y }, false, extend);
      if (!next) return null;
      current = next;
    }
  }
  return current;
}

export function chainWallsToPolygon(wallIds: string[], walls: Wall[]): FloorPlanPoint[] | null {
  const segs = wallIds
    .map((id) => walls.find((w) => w.id === id))
    .filter((w): w is Wall => !!w);
  if (segs.length < 3) return null;

  const used = new Set<string>([segs[0].id]);
  const pts: FloorPlanPoint[] = [{ ...segs[0].a }, { ...segs[0].b }];
  let current = segs[0].b;

  while (used.size < segs.length) {
    let next: Wall | null = null;
    let nextPt: FloorPlanPoint | null = null;
    for (let i = 0; i < segs.length; i++) {
      const seg = segs[i];
      if (used.has(seg.id)) continue;
      if (pointsEqual(seg.a, current, 6)) {
        next = seg;
        nextPt = seg.b;
        break;
      }
      if (pointsEqual(seg.b, current, 6)) {
        next = seg;
        nextPt = seg.a;
        break;
      }
    }
    if (!next || !nextPt) break;
    used.add(next.id);
    pts.push({ ...nextPt });
    current = nextPt;
  }

  if (pts.length > 1 && pointsEqual(pts[0], pts[pts.length - 1], 6)) {
    pts.pop();
  }
  if (used.size < segs.length) return null;
  return pts.length >= 3 ? pts : null;
}

export function rebuildRoomPolygons(rooms: Room[], walls: Wall[]): Room[] {
  if (rooms.length === 0) return rooms;
  const cycles = getWallCycles(walls);
  const used = new Set<string>();
  const byId = new Map<string, Room>();

  const ranked = rooms
    .map((room, index) => ({ room, index, area: Math.abs(polygonArea(room.polygon)) }))
    .sort((a, b) => a.area - b.area);

  for (let r = 0; r < ranked.length; r++) {
    const { room } = ranked[r];
    const matched = matchEnclosedCycleToRoom(room, cycles, used);
    if (matched) {
      const sig = [...matched.originalWallIds].sort().join(',');
      used.add(sig);
      byId.set(room.id, { ...room, polygon: matched.polygon, wallIds: matched.originalWallIds });
    } else {
      const chained = chainWallsToPolygon(room.wallIds, walls);
      byId.set(room.id, chained ? { ...room, polygon: chained } : room);
    }
  }

  return rooms.map((room) => byId.get(room.id) ?? room);
}

/** After deleting walls, merge rooms that shared a removed partition into the largest room. */
export function applyDeletedWallsToRooms(
  rooms: Room[],
  remainingWalls: Wall[],
  deletedIds: Set<string>
): Room[] {
  if (rooms.length === 0 || deletedIds.size === 0) return rooms;

  const affected: Room[] = [];
  const kept: Room[] = [];
  for (let i = 0; i < rooms.length; i++) {
    const room = rooms[i];
    if (room.wallIds.some((id) => deletedIds.has(id))) affected.push(room);
    else kept.push(room);
  }
  if (affected.length === 0) return rooms;
  if (affected.length === 1) return kept;

  let survivor = affected[0];
  let survivorArea = Math.abs(polygonArea(survivor.polygon));
  for (let i = 1; i < affected.length; i++) {
    const area = Math.abs(polygonArea(affected[i].polygon));
    if (area > survivorArea) {
      survivor = affected[i];
      survivorArea = area;
    }
  }

  const mergedIds: string[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < affected.length; i++) {
    const ids = affected[i].wallIds;
    for (let j = 0; j < ids.length; j++) {
      const id = ids[j];
      if (deletedIds.has(id) || seen.has(id)) continue;
      seen.add(id);
      mergedIds.push(id);
    }
  }

  const cycles = getWallCycles(remainingWalls);
  const matched = matchCycleToRoom({ ...survivor, wallIds: mergedIds }, cycles);
  if (matched) {
    return [...kept, { ...survivor, polygon: matched.polygon, wallIds: matched.originalWallIds }];
  }
  const seed = polygonCentroid(survivor.polygon);
  const loop = findClosedLoopAtPoint(seed, remainingWalls);
  if (loop) {
    return [...kept, { ...survivor, polygon: loop.polygon, wallIds: loop.wallIds }];
  }
  return kept;
}

export function remapOpeningsForDrag(
  openings: Opening[],
  oldWalls: Wall[],
  newWalls: Wall[],
  pixelsPerMeter = DEFAULT_PIXELS_PER_METER
): Opening[] {
  const oldById = new Map(oldWalls.map((w) => [w.id, w]));
  return openings.map((opening) => {
    const oldWall = oldById.get(opening.wallId);
    if (!oldWall) return opening;
    const world = pointAlongWall(oldWall, opening.t);
    let best: Wall | null = null;
    let bestDist = 12;
    let bestT = opening.t;
    for (let i = 0; i < newWalls.length; i++) {
      const candidate = newWalls[i];
      const sameFamily =
        candidate.id === opening.wallId ||
        candidate.id.startsWith(`${opening.wallId}~ext~`);
      const proj = projectPointOnSegment(world, candidate.a, candidate.b);
      const score = sameFamily ? proj.distance - 4 : proj.distance;
      if (score < bestDist) {
        bestDist = score;
        best = candidate;
        bestT = proj.t;
      }
    }
    if (!best) return opening;
    return {
      ...opening,
      wallId: best.id,
      t: clampDoorT(best, opening.widthM, bestT, pixelsPerMeter),
    };
  });
}

function extendSplitId(baseId: string, a: FloorPlanPoint, b: FloorPlanPoint): string {
  const ka = vertexKey(a);
  const kb = vertexKey(b);
  const lo = ka < kb ? ka : kb;
  const hi = ka < kb ? kb : ka;
  return `${baseId}~ext~${lo}~${hi}`;
}

type JunctionMark = { t: number; point: FloorPlanPoint };

function junctionMarksOnWall(walls: Wall[], wall: Wall): JunctionMark[] {
  const marks: JunctionMark[] = [
    { t: 0, point: wall.a },
    { t: 1, point: wall.b },
  ];
  for (let o = 0; o < walls.length; o++) {
    const other = walls[o];
    if (other.id === wall.id) continue;
    const ends: Array<'a' | 'b'> = ['a', 'b'];
    for (let e = 0; e < ends.length; e++) {
      const p = other[ends[e]];
      const proj = projectPointOnSegment(p, wall.a, wall.b);
      if (proj.distance > T_JUNCTION_HIT_PX) continue;
      if (proj.t < 0.04 || proj.t > 0.96) continue;
      marks.push({ t: proj.t, point: { ...p } });
    }
    const cross = segmentIntersection(wall.a, wall.b, other.a, other.b);
    if (cross) marks.push({ t: cross.tA, point: { ...cross.point } });
  }
  marks.sort((x, y) => x.t - y.t);
  const unique: JunctionMark[] = [];
  for (let i = 0; i < marks.length; i++) {
    const m = marks[i];
    const last = unique[unique.length - 1];
    if (last && Math.abs(last.t - m.t) < 0.015) continue;
    unique.push(m);
  }
  return unique;
}

export function wallCanSplit(walls: Wall[], wallId: string): boolean {
  const wall = walls.find((w) => w.id === wallId);
  if (!wall) return false;
  return junctionMarksOnWall(walls, wall).length > 2;
}

function splitOneWallAtMarks(
  walls: Wall[],
  wallId: string
): { walls: Wall[]; segmentIds: string[] } | null {
  const wall = walls.find((w) => w.id === wallId);
  if (!wall) return null;
  const unique = junctionMarksOnWall(walls, wall);
  if (unique.length <= 2) return null;
  const segments: Wall[] = [];
  for (let i = 0; i < unique.length - 1; i++) {
    const segA = unique[i].point;
    const segB = unique[i + 1].point;
    if (pixelDistance(segA, segB) < MIN_WALL_LENGTH_PX) continue;
    segments.push({
      id: segments.length === 0 ? wall.id : extendSplitId(wall.id, segA, segB),
      a: { ...segA },
      b: { ...segB },
      thicknessM: wall.thicknessM,
    });
  }
  if (segments.length < 2) return null;
  return {
    walls: walls.filter((w) => w.id !== wallId).concat(segments),
    segmentIds: segments.map((s) => s.id),
  };
}

/** Split one wall at every T-junction and crossing along it. Also splits walls it crosses. */
export function splitWallAtTJunctions(
  walls: Wall[],
  wallId: string
): { walls: Wall[]; segmentIds: string[] } | { error: string } {
  const wall = walls.find((w) => w.id === wallId);
  if (!wall) return { error: 'Could not find that wall.' };

  const related: string[] = [wallId];
  for (let i = 0; i < walls.length; i++) {
    const other = walls[i];
    if (other.id === wallId) continue;
    if (segmentIntersection(wall.a, wall.b, other.a, other.b)) related.push(other.id);
  }

  let current = walls;
  let segmentIds: string[] = [];
  for (let i = 0; i < related.length; i++) {
    const split = splitOneWallAtMarks(current, related[i]);
    if (!split) continue;
    current = split.walls;
    if (related[i] === wallId) segmentIds = split.segmentIds;
  }
  if (segmentIds.length < 2) {
    return {
      error: 'This wall has no junction to split at. Another wall must meet it in the middle.',
    };
  }
  return { walls: current, segmentIds };
}

/**
 * After an extend drag, a stretched wall may run past a T-junction.
 * Split the overhang into its own wall (user can merge later).
 */
export function splitStretchedWallsAtTJunctions(oldWalls: Wall[], newWalls: Wall[]): Wall[] {
  const oldById = new Map(oldWalls.map((w) => [w.id, w]));
  const kept: Wall[] = [];
  const created: Wall[] = [];

  for (let w = 0; w < newWalls.length; w++) {
    const wall = newWalls[w];
    const prev = oldById.get(wall.id);
    if (!prev) {
      kept.push(wall);
      continue;
    }
    const aMoved = !pointsEqual(prev.a, wall.a, 1);
    const bMoved = !pointsEqual(prev.b, wall.b, 1);
    if (aMoved === bMoved) {
      kept.push(wall);
      continue;
    }

    const unique = junctionMarksOnWall(newWalls, wall);
    if (unique.length <= 2) {
      kept.push(wall);
      continue;
    }

    const unmoved = aMoved ? wall.b : wall.a;
    let keptOriginal = false;
    for (let i = 0; i < unique.length - 1; i++) {
      const segA = unique[i].point;
      const segB = unique[i + 1].point;
      if (pixelDistance(segA, segB) < MIN_WALL_LENGTH_PX) continue;
      const isOriginal =
        !keptOriginal &&
        (pointsEqual(segA, unmoved, 3) || pointsEqual(segB, unmoved, 3));
      const seg: Wall = {
        id: isOriginal ? wall.id : extendSplitId(wall.id, segA, segB),
        a: { ...segA },
        b: { ...segB },
        thicknessM: wall.thicknessM,
      };
      if (isOriginal) {
        keptOriginal = true;
        kept.push(seg);
      } else {
        created.push(seg);
      }
    }
    if (!keptOriginal) kept.push(wall);
  }

  return kept.concat(created);
}

export type ConnectedWallDragArgs = {
  mode: 'push' | 'corner';
  wallId?: string;
  vertexPoint?: FloorPlanPoint;
  delta: FloorPlanPoint;
  freeAngle?: boolean;
  extend?: boolean;
  /** Skip persist-only T-junction splits so wall ids stay stable during a live drag. */
  preview?: boolean;
};

export function applyConnectedWallDragToPlan(
  fp: DesignFloorPlan,
  args: ConnectedWallDragArgs
): DesignFloorPlan | null {
  const extend = !!args.extend;
  const nextWalls =
    args.mode === 'push'
      ? args.wallId
        ? pushWall(fp.walls, args.wallId, args.delta, !!args.freeAngle, extend)
        : null
      : args.vertexPoint
        ? moveCorner(
            fp.walls,
            args.vertexPoint,
            args.delta,
            !!args.freeAngle,
            extend,
            args.wallId
          )
        : null;
  if (!nextWalls) return null;
  const walls =
    extend && !args.preview
      ? splitStretchedWallsAtTJunctions(fp.walls, nextWalls)
      : nextWalls;
  const ppm = fp.pixelsPerMeter || DEFAULT_PIXELS_PER_METER;
  return {
    ...fp,
    walls,
    openings: remapOpeningsForDrag(fp.openings, fp.walls, walls, ppm),
    rooms: rebuildRoomPolygons(fp.rooms, walls),
  };
}

/** Stretch a wall to a target length (meters). The less-connected end moves; junctions follow like a corner drag. */
export function applyWallLengthToPlan(
  fp: DesignFloorPlan,
  wallId: string,
  targetMeters: number
): DesignFloorPlan | null {
  const wall = fp.walls.find((w) => w.id === wallId);
  if (!wall) return null;
  const ppm = fp.pixelsPerMeter || DEFAULT_PIXELS_PER_METER;
  const targetPx = Math.max(MIN_WALL_LENGTH_PX, targetMeters * ppm);
  const current = wallLengthPx(wall);
  const dL = targetPx - current;
  if (Math.abs(dL) < 0.05) return fp;
  const dir = unitDirection(wall.a, wall.b);
  if (!dir) return null;

  const clusters = clusterVertices(fp.walls);
  const clusterA = clusterAtPoint(clusters, wall.a);
  const clusterB = clusterAtPoint(clusters, wall.b);
  const degreeA = clusterA?.refs.length ?? 1;
  const degreeB = clusterB?.refs.length ?? 1;
  const moveB = degreeB <= degreeA;
  const vertex = moveB ? wall.b : wall.a;
  const delta = moveB
    ? { x: dir.x * dL, y: dir.y * dL }
    : { x: -dir.x * dL, y: -dir.y * dL };

  const lengthOf = (walls: Wall[]) => {
    const next = walls.find((w) => w.id === wallId);
    return next ? wallLengthPx(next) : -1;
  };

  let nextWalls = moveCorner(fp.walls, vertex, delta, false);
  if (!nextWalls || Math.abs(lengthOf(nextWalls) - targetPx) > 0.5) {
    const cluster = moveB ? clusterB : clusterA;
    if (cluster && cluster.refs.length > 1) {
      nextWalls = applyDeltaToRefs(fp.walls, cluster.refs, delta);
    } else {
      nextWalls = fp.walls.map((w) => {
        if (w.id !== wallId) return w;
        if (moveB) {
          return {
            ...w,
            b: { x: wall.a.x + dir.x * targetPx, y: wall.a.y + dir.y * targetPx },
          };
        }
        return {
          ...w,
          a: { x: wall.b.x - dir.x * targetPx, y: wall.b.y - dir.y * targetPx },
        };
      });
    }
    if (anyWallTooShort(nextWalls)) return null;
    if (Math.abs(lengthOf(nextWalls) - targetPx) > 0.5) return null;
  }

  return {
    ...fp,
    walls: nextWalls,
    openings: remapOpeningsForDrag(fp.openings, fp.walls, nextWalls, ppm),
    rooms: rebuildRoomPolygons(fp.rooms, nextWalls),
  };
}

/**
 * Split walls at T-junctions / mid-wall connections so adjacent rooms that share
 * only part of a wall can form closed loops.
 */
export function splitWallsAtJunctions(walls: Wall[]): Wall[] {
  if (walls.length === 0) return walls;

  const result: Wall[] = [];

  for (const wall of walls) {
    const len = wallLengthPx(wall);
    if (len < 1) continue;

    const uniqueMarks = junctionMarksOnWall(walls, wall);
    if (uniqueMarks.length <= 2) {
      result.push(wall);
      continue;
    }

    for (let i = 0; i < uniqueMarks.length - 1; i++) {
      const a = uniqueMarks[i].point;
      const b = uniqueMarks[i + 1].point;
      if (pixelDistance(a, b) < 1) continue;
      result.push({
        id: `${wall.id}::${i}`,
        a,
        b,
        thicknessM: wall.thicknessM,
      });
    }
  }

  return result;
}

/** Original wall id before junction split suffix */
export function originalWallId(wallId: string): string {
  const idx = wallId.indexOf('::');
  return idx === -1 ? wallId : wallId.slice(0, idx);
}

type WallCycle = {
  polygon: FloorPlanPoint[];
  originalWallIds: string[];
};

let cyclesCacheWalls: Wall[] | null = null;
let cyclesCache: WallCycle[] = [];

function getWallCycles(walls: Wall[]): WallCycle[] {
  if (cyclesCacheWalls === walls) return cyclesCache;
  cyclesCacheWalls = walls;
  cyclesCache = enumerateWallCycles(walls);
  return cyclesCache;
}

function enumerateWallCycles(walls: Wall[]): WallCycle[] {
  if (walls.length < 3) return [];
  const graphWalls = splitWallsAtJunctions(walls);
  if (graphWalls.length < 3) return [];

  type NodeKey = string;
  type AdjEdge = { to: NodeKey; wallId: string; angle: number };
  const keyOf = (p: FloorPlanPoint): NodeKey =>
    `${Math.round(p.x / 2) * 2},${Math.round(p.y / 2) * 2}`;

  const nodePoints = new Map<NodeKey, FloorPlanPoint>();
  const adj = new Map<NodeKey, AdjEdge[]>();

  const addDir = (from: FloorPlanPoint, to: FloorPlanPoint, wallId: string) => {
    const fk = keyOf(from);
    const tk = keyOf(to);
    if (fk === tk) return;
    nodePoints.set(fk, from);
    nodePoints.set(tk, to);
    if (!adj.has(fk)) adj.set(fk, []);
    adj.get(fk)!.push({
      to: tk,
      wallId,
      angle: Math.atan2(to.y - from.y, to.x - from.x),
    });
  };

  for (let i = 0; i < graphWalls.length; i++) {
    const wall = graphWalls[i];
    if (wallLengthPx(wall) < 1) continue;
    addDir(wall.a, wall.b, wall.id);
    addDir(wall.b, wall.a, wall.id);
  }

  adj.forEach((nbrs) => {
    nbrs.sort((a, b) => a.angle - b.angle);
  });

  const used = new Set<string>();
  const dirKey = (from: NodeKey, to: NodeKey, wallId: string) => `${from}|${to}|${wallId}`;
  const faces: WallCycle[] = [];
  const maxSteps = Math.max(12, graphWalls.length * 2);

  adj.forEach((nbrs, start) => {
    for (let i = 0; i < nbrs.length; i++) {
      const first = nbrs[i];
      if (used.has(dirKey(start, first.to, first.wallId))) continue;

      const nodes: NodeKey[] = [start];
      const wallIds: string[] = [];
      let curr = start;
      let edge = first;
      let closed = false;

      for (let step = 0; step < maxSteps; step++) {
        const k = dirKey(curr, edge.to, edge.wallId);
        if (step > 0 && used.has(k)) break;
        used.add(k);
        wallIds.push(edge.wallId);
        nodes.push(edge.to);

        const at = edge.to;
        const atNbrs = adj.get(at) || [];
        let incoming = -1;
        for (let j = 0; j < atNbrs.length; j++) {
          if (atNbrs[j].to === curr && atNbrs[j].wallId === edge.wallId) {
            incoming = j;
            break;
          }
        }
        if (incoming < 0) {
          for (let j = 0; j < atNbrs.length; j++) {
            if (atNbrs[j].to === curr) {
              incoming = j;
              break;
            }
          }
        }
        if (incoming < 0 || atNbrs.length === 0) break;

        const nextEdge = atNbrs[(incoming - 1 + atNbrs.length) % atNbrs.length];
        curr = at;
        edge = nextEdge;
        if (curr === start && edge.to === first.to && edge.wallId === first.wallId) {
          closed = true;
          break;
        }
      }

      if (!closed || nodes.length < 4) continue;
      if (nodes[0] === nodes[nodes.length - 1]) nodes.pop();
      if (nodes.length < 3) continue;
      const polygon = nodes.map((nk) => nodePoints.get(nk)!).filter(Boolean) as FloorPlanPoint[];
      if (polygon.length < 3) continue;
      if (Math.abs(polygonArea(polygon)) < 8) continue;
      faces.push({
        polygon,
        originalWallIds: Array.from(new Set(wallIds.map(originalWallId))),
      });
    }
  });

  if (faces.length > 1) {
    let largestI = 0;
    let largest = 0;
    for (let i = 0; i < faces.length; i++) {
      const a = Math.abs(polygonArea(faces[i].polygon));
      if (a > largest) {
        largest = a;
        largestI = i;
      }
    }
    faces.splice(largestI, 1);
  }

  return faces;
}

function roomOwnsWallId(room: Room, wallId: string): boolean {
  const root = originalWallId(wallId);
  for (let i = 0; i < room.wallIds.length; i++) {
    const id = room.wallIds[i];
    if (id === wallId || id === root) return true;
    if (wallId.startsWith(`${id}~ext~`) || root.startsWith(`${id}~ext~`)) return true;
  }
  return false;
}

function matchEnclosedCycleToRoom(
  room: Room,
  cycles: WallCycle[],
  used: Set<string>
): WallCycle | null {
  const seed = room.polygon.length >= 3 ? polygonCentroid(room.polygon) : null;
  let best: WallCycle | null = null;
  let bestScore = Infinity;

  for (let i = 0; i < cycles.length; i++) {
    const cycle = cycles[i];
    const sig = [...cycle.originalWallIds].sort().join(',');
    if (used.has(sig)) continue;
    const area = Math.abs(polygonArea(cycle.polygon));
    if (area < 8) continue;
    const containsSeed = !!(seed && pointInPolygon(seed, cycle.polygon));
    let inside = 0;
    for (let p = 0; p < room.polygon.length; p++) {
      if (pointInPolygon(room.polygon[p], cycle.polygon)) inside++;
    }
    const frac = room.polygon.length > 0 ? inside / room.polygon.length : 0;
    if (!containsSeed && frac < 0.4) continue;
    const score = area - (containsSeed ? 1e7 : 0) - frac * 1e5;
    if (score < bestScore) {
      bestScore = score;
      best = cycle;
    }
  }
  return best;
}

function matchCycleToRoom(room: Room, cycles: WallCycle[]): WallCycle | null {
  const seed = room.polygon.length > 0 ? polygonCentroid(room.polygon) : null;
  let best: WallCycle | null = null;
  let bestScore = Infinity;

  for (let i = 0; i < cycles.length; i++) {
    const cycle = cycles[i];
    let owned = 0;
    let foreign = 0;
    for (let j = 0; j < cycle.originalWallIds.length; j++) {
      if (roomOwnsWallId(room, cycle.originalWallIds[j])) owned++;
      else foreign++;
    }
    if (foreign > 0 || owned < 3) continue;
    const area = Math.abs(polygonArea(cycle.polygon));
    const dist = seed ? pixelDistance(seed, polygonCentroid(cycle.polygon)) : 0;
    const missing = Math.max(0, room.wallIds.length - owned);
    const score = foreign * 1e9 + missing * 1e6 + area + dist;
    if (score < bestScore) {
      bestScore = score;
      best = cycle;
    }
  }
  return best;
}

/** Build adjacency graph of wall endpoints and find a closed loop containing a point */
export function findClosedLoopAtPoint(
  point: FloorPlanPoint,
  walls: Wall[]
): { polygon: FloorPlanPoint[]; wallIds: string[] } | null {
  const cycles = getWallCycles(walls);
  let best: WallCycle | null = null;
  let bestArea = Infinity;
  for (let i = 0; i < cycles.length; i++) {
    const cycle = cycles[i];
    if (!pointInPolygon(point, cycle.polygon)) continue;
    const area = Math.abs(polygonArea(cycle.polygon));
    if (area < bestArea) {
      bestArea = area;
      best = cycle;
    }
  }
  if (!best) return null;
  return { polygon: best.polygon, wallIds: best.originalWallIds };
}

export function pointInPolygon(point: FloorPlanPoint, polygon: FloorPlanPoint[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    const intersect =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi + 0.0000001) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function polygonArea(polygon: FloorPlanPoint[]): number {
  let area = 0;
  for (let i = 0; i < polygon.length; i++) {
    const j = (i + 1) % polygon.length;
    area += polygon[i].x * polygon[j].y;
    area -= polygon[j].x * polygon[i].y;
  }
  return area / 2;
}

/** Area-weighted centroid (correct even when edges have extra T-junction vertices). */
export function polygonCentroid(polygon: FloorPlanPoint[]): FloorPlanPoint {
  if (polygon.length === 0) return { x: 0, y: 0 };
  if (polygon.length < 3) {
    let x = 0;
    let y = 0;
    for (const p of polygon) {
      x += p.x;
      y += p.y;
    }
    return { x: x / polygon.length, y: y / polygon.length };
  }

  let cx = 0;
  let cy = 0;
  let area2 = 0;
  for (let i = 0; i < polygon.length; i++) {
    const j = (i + 1) % polygon.length;
    const cross = polygon[i].x * polygon[j].y - polygon[j].x * polygon[i].y;
    area2 += cross;
    cx += (polygon[i].x + polygon[j].x) * cross;
    cy += (polygon[i].y + polygon[j].y) * cross;
  }

  if (Math.abs(area2) < 1e-6) {
    let x = 0;
    let y = 0;
    for (const p of polygon) {
      x += p.x;
      y += p.y;
    }
    return { x: x / polygon.length, y: y / polygon.length };
  }

  return {
    x: cx / (3 * area2),
    y: cy / (3 * area2),
  };
}

export function calibratePixelsPerMeter(wallPxLength: number, realMeters: number): number {
  if (realMeters <= 0 || wallPxLength <= 0) return DEFAULT_PIXELS_PER_METER;
  return wallPxLength / realMeters;
}

/** Convert length in display units to meters */
export function lengthToMeters(
  value: number,
  unit: 'm' | 'cm' | 'ft' | 'in'
): number {
  switch (unit) {
    case 'm':
      return value;
    case 'cm':
      return value / 100;
    case 'ft':
      return value * 0.3048;
    case 'in':
      return value * 0.0254;
    default:
      return value;
  }
}

/** Format plan length for UI — always meters or feet (never cm / inches). */
export function formatMeters(
  meters: number,
  units: 'metric' | 'imperial',
  decimals = 2
): string {
  if (units === 'imperial') {
    const feet = meters / 0.3048;
    return `${trimFixed(feet, decimals)} ft`;
  }
  return `${trimFixed(meters, decimals)} m`;
}

export function formatMetersValue(
  meters: number,
  units: 'metric' | 'imperial',
  decimals = 2
): string {
  if (units === 'imperial') {
    return trimFixed(meters / 0.3048, decimals);
  }
  return trimFixed(meters, decimals);
}

function trimFixed(value: number, decimals: number): string {
  const fixed = value.toFixed(decimals);
  // "5.00" → "5", "0.50" → "0.5"
  return fixed.replace(/\.?0+$/, '') || '0';
}

export function isLine(el: DrawingElement): el is Line {
  return 'start' in el && 'end' in el;
}

export function isRectangle(el: DrawingElement): el is Rectangle {
  return 'topLeft' in el && 'bottomRight' in el;
}

/** Migrate legacy FloorplanData (lines/rects) into DesignFloorPlan walls */
export function migrateFloorplanDataToDesignFloorPlan(
  data: FloorplanData
): DesignFloorPlan {
  const walls: Wall[] = [];
  // Always use the fixed display scale — legacy DPI-style scales were life-sized
  const pixelsPerMeter = DEFAULT_PIXELS_PER_METER;

  const units: 'metric' | 'imperial' =
    data.settings?.measurementSystem === 'imperial' ? 'imperial' : 'metric';

  for (const el of data.elements || []) {
    if (isLine(el)) {
      walls.push({
        id: el.id || `wall-${walls.length}`,
        a: { x: el.start.x, y: el.start.y },
        b: { x: el.end.x, y: el.end.y },
      });
    } else if (isRectangle(el)) {
      const corners = [el.topLeft, el.topRight, el.bottomRight, el.bottomLeft];
      for (let i = 0; i < 4; i++) {
        const a = corners[i];
        const b = corners[(i + 1) % 4];
        walls.push({
          id: `${el.id}-side-${i}`,
          a: { x: a.x, y: a.y },
          b: { x: b.x, y: b.y },
        });
      }
    }
  }

  return {
    version: 1,
    units,
    pixelsPerMeter,
    calibrated: walls.length > 0,
    walls,
    openings: [],
    rooms: [],
  };
}

export function openingEndpoints(
  wall: Wall,
  opening: Opening,
  pixelsPerMeter: number
): { start: FloorPlanPoint; end: FloorPlanPoint; center: FloorPlanPoint } {
  const wallPx = wallLengthPx(wall);
  const halfWidthPx = (opening.widthM * pixelsPerMeter) / 2;
  const halfT = wallPx > 0 ? halfWidthPx / wallPx : 0;
  const t0 = Math.max(0, opening.t - halfT);
  const t1 = Math.min(1, opening.t + halfT);
  return {
    start: pointAlongWall(wall, t0),
    end: pointAlongWall(wall, t1),
    center: pointAlongWall(wall, opening.t),
  };
}

/** Keep a door fully on the wall (not hanging past corners). */
export function clampDoorT(
  wall: Wall,
  widthM: number,
  t: number,
  pixelsPerMeter: number
): number {
  const wallPx = wallLengthPx(wall);
  if (wallPx < 1) return 0.5;
  const halfWidthPx = (widthM * pixelsPerMeter) / 2;
  const halfT = Math.min(0.49, halfWidthPx / wallPx);
  return Math.max(halfT, Math.min(1 - halfT, t));
}

export function doorFitsOnWall(
  wall: Wall,
  widthM: number,
  pixelsPerMeter: number
): boolean {
  return wallLengthPx(wall) >= widthM * pixelsPerMeter * 0.95;
}

export type DoorHinge = 'start' | 'end';
export type DoorSwingSide = 1 | -1;

export const DOOR_SWING_CYCLE: Array<{ hinge: DoorHinge; swingSide: DoorSwingSide }> = [
  { hinge: 'start', swingSide: 1 },
  { hinge: 'end', swingSide: 1 },
  { hinge: 'start', swingSide: -1 },
  { hinge: 'end', swingSide: -1 },
];

export function nextDoorSwing(
  hinge: DoorHinge = 'start',
  swingSide: DoorSwingSide = 1
): { hinge: DoorHinge; swingSide: DoorSwingSide } {
  const idx = DOOR_SWING_CYCLE.findIndex(
    (c) => c.hinge === hinge && c.swingSide === swingSide
  );
  return DOOR_SWING_CYCLE[(idx + 1 + DOOR_SWING_CYCLE.length) % DOOR_SWING_CYCLE.length];
}

/** Geometry for drawing a door leaf + swing arc. */
export function doorSwingGeometry(
  wall: Wall,
  opening: Pick<Opening, 't' | 'widthM' | 'hinge' | 'swingSide'>,
  pixelsPerMeter: number
): {
  start: FloorPlanPoint;
  end: FloorPlanPoint;
  center: FloorPlanPoint;
  hinge: FloorPlanPoint;
  latch: FloorPlanPoint;
  open: FloorPlanPoint;
  radius: number;
  sweepFlag: 0 | 1;
} {
  const { start, end, center } = openingEndpoints(wall, opening as Opening, pixelsPerMeter);
  const dx = wall.b.x - wall.a.x;
  const dy = wall.b.y - wall.a.y;
  const len = Math.hypot(dx, dy) || 1;
  const side = opening.swingSide ?? 1;
  const nx = (-dy / len) * side;
  const ny = (dx / len) * side;
  const hingeAtStart = (opening.hinge ?? 'start') === 'start';
  const hinge = hingeAtStart ? start : end;
  const latch = hingeAtStart ? end : start;
  const radius = Math.hypot(latch.x - hinge.x, latch.y - hinge.y) || 18;
  const open = { x: hinge.x + nx * radius, y: hinge.y + ny * radius };
  const cross =
    (latch.x - hinge.x) * (open.y - hinge.y) - (latch.y - hinge.y) * (open.x - hinge.x);
  const sweepFlag: 0 | 1 = cross > 0 ? 1 : 0;
  return { start, end, center, hinge, latch, open, radius, sweepFlag };
}

export function roomAreaMeters(room: Room, pixelsPerMeter: number): number {
  if (pixelsPerMeter <= 0) return 0;
  const areaPx = Math.abs(polygonArea(room.polygon));
  return areaPx / (pixelsPerMeter * pixelsPerMeter);
}
